import { useState, useRef, useEffect } from 'react';
import { Plus, MessageSquare, MoreVertical, CreditCard as Edit3, Trash2, PanelLeftClose, PanelLeft, Search } from 'lucide-react';
import type { Chat } from '../types/chat';

interface ChatSidebarProps {
  chats: Chat[];
  currentChatId: string | null;
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onRenameChat: (chatId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export function ChatSidebar({
  chats,
  currentChatId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  isOpen,
  onToggle,
}: ChatSidebarProps) {
  const [menuOpenForChat, setMenuOpenForChat] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenForChat(null);
      }
    }

    if (menuOpenForChat) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpenForChat]);

  const handleMenuToggle = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    setMenuOpenForChat(menuOpenForChat === chatId ? null : chatId);
  };

  const handleMenuAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
    setMenuOpenForChat(null);
  };

  const filteredChats = chats.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) {
    return (
      <div className="flex w-12 flex-col items-center gap-2 border-r border-slate-200 bg-white py-4 dark:border-slate-800 dark:bg-slate-900">
        <button
          onClick={onToggle}
          className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          <PanelLeft className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full w-64 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 p-3 dark:border-slate-800">
        <button
          onClick={onNewChat}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <Plus className="w-4 h-4" />
          <span>New chat</span>
        </button>
        <button
          onClick={onToggle}
          className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
      </div>

      <div className="px-3 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 transform text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            placeholder="Search chats"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1 mt-2">
        {filteredChats.map((chat) => (
          <div
            key={chat.id}
            className={`group relative -ml-0.5 flex cursor-pointer items-center gap-2 rounded-lg border-l-2 border-transparent px-3 py-2 pl-2.5 transition-colors dark:border-transparent ${
              currentChatId === chat.id
                ? 'border-slate-800 bg-slate-100 font-medium text-slate-900 dark:border-slate-200 dark:bg-slate-800 dark:text-slate-100'
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/80'
            }`}
            onClick={() => onSelectChat(chat.id)}
          >
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 truncate text-sm">{chat.title}</span>
            <button
              onClick={(e) => handleMenuToggle(e, chat.id)}
              className="rounded p-1 opacity-0 transition-all hover:bg-slate-200 group-hover:opacity-100 dark:hover:bg-slate-700"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {menuOpenForChat === chat.id && (
              <div
                ref={menuRef}
                className="absolute right-0 top-full z-50 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => handleMenuAction(e, () => onRenameChat(chat.id))}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <Edit3 className="w-4 h-4" />
                  <span className="text-sm">Rename</span>
                </button>
                <button
                  onClick={(e) => handleMenuAction(e, () => onDeleteChat(chat.id))}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-red-600 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">Delete</span>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
